import { apiFetch } from '../../api';
import { ensureSocketConnection } from '../../websocket/socketUtils';

// ─── Socket parse helper ──────────────────────────────────────────────────────
const parseSocket = (msg) => {
    const payload = JSON.parse(msg.body);
    return typeof payload === 'string' ? JSON.parse(payload) : payload;
};

// ─── Endpoint registry ────────────────────────────────────────────────────────
const CONFIG_ENDPOINTS = {
    save: {
        rest: '/saveWhatsAppConfig',
        socketApp: '/app/saveWhatsAppConfig',
        socketTopic: (loginId) => `/topic/saveWhatsAppConfig/${loginId}`,
        method: 'POST',
    },
    update: {
        rest: '/updateWhatsAppConfig',
        socketApp: '/app/updateWhatsAppConfig',
        socketTopic: (loginId) => `/topic/updateWhatsAppConfig/${loginId}`,
        method: 'PUT',
    },
};

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIG CRUD
// ═══════════════════════════════════════════════════════════════════════════════

export const fetchAllConfigs = async ({ deliveryMode, client, loginId }) => {
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(`/topic/getAllWhatsAppConfig/${loginId}`, (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS' && Array.isArray(data.data)) resolve(data.data);
                else reject(new Error(data.message || 'Failed to load configs'));
            });
            client.publish({ destination: '/app/getAllWhatsAppConfig', body: JSON.stringify({}) });
        });
    }
    const res = await apiFetch('/getAllWhatsAppConfig');
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return data.data;
    throw new Error(data.message || 'Failed to load configs');
};

export const saveConfig = async ({ deliveryMode, client, loginId, formData, isEditing, configId }) => {
    const action = isEditing ? 'update' : 'save';
    const endpoint = CONFIG_ENDPOINTS[action];
    const body = { ...formData, ...(isEditing && { _id: configId }) };

    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(endpoint.socketTopic(loginId), (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') resolve(data);
                else reject(new Error(data.message || `Failed to ${action} config`));
            });
            client.publish({ destination: endpoint.socketApp, body: JSON.stringify(body) });
        });
    }
    const res = await apiFetch(endpoint.rest, {
        method: endpoint.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
    });
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return data;
    throw new Error(data.message || `Failed to ${action} config`);
};

export const toggleConfigStatus = async ({ deliveryMode, client, loginId, configId, currentStatus, licenseNumber }) => {
    const active = currentStatus !== 'ACTIVE';
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(`/topic/activeOrDeActiveWhatsAppConfig/${loginId}`, (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') resolve(data);
                else reject(new Error(data.message || 'Failed to update status'));
            });
            client.publish({ destination: '/app/activeOrDeActiveWhatsAppConfig', body: JSON.stringify({ id: configId, active, licenseNumber }) });
        });
    }
    const res = await apiFetch(`/activeOrDeActiveWhatsAppConfig?id=${configId}&active=${active}&licenseNumber=${licenseNumber}`, { method: 'PUT' });
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return data;
    throw new Error(data.message || 'Failed to update status');
};

// ═══════════════════════════════════════════════════════════════════════════════
// QUEUE
// ═══════════════════════════════════════════════════════════════════════════════

export const fetchQueueType = async ({ deliveryMode, client, loginId }) => {
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(`/topic/whatsapp/running/queue/${loginId}`, (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') resolve(data.data.toString());
                else reject(new Error('Failed to get queue type'));
            });
            client.publish({ destination: '/app/whatsapp/running/queue', body: JSON.stringify({ type: '0' }) });
        });
    }
    const res = await apiFetch('/whatsapp/running/queue?type=0');
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return data.data.toString();
    throw new Error('Failed to get queue type');
};

export const updateQueueType = async ({ deliveryMode, client, loginId, queueType }) => {
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(`/topic/active-or-de-active/whatsapp/queue/${loginId}`, (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') resolve(data);
                else reject(new Error(data.MESSAGE || 'Failed to update queue type'));
            });
            client.publish({ destination: '/app/active-or-de-active/whatsapp/queue', body: JSON.stringify({ type: queueType }) });
        });
    }
    const res = await apiFetch(`/active-or-de-active/whatsapp/queue?type=${queueType}`, { method: 'POST' });
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return data;
    throw new Error(data.MESSAGE || 'Failed to update queue type');
};

// ═══════════════════════════════════════════════════════════════════════════════
// APP NAME STATUS
// ═══════════════════════════════════════════════════════════════════════════════

export const fetchAppNameStatuses = async ({ deliveryMode, client, loginId }) => {
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(`/topic/get/all/whatsapp/appName/status/${loginId}`, (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS' && data.DATA) resolve(data.DATA);
                else reject(new Error('Failed to get app statuses'));
            });
            client.publish({ destination: '/app/get/all/whatsapp/appName/status', body: JSON.stringify({}) });
        });
    }
    const res = await apiFetch('/get/all/whatsapp/appName/status');
    const data = await res.json();
    if (data.STATUS === 'SUCCESS' && data.DATA) return data.DATA;
    throw new Error('Failed to get app statuses');
};

export const toggleAppNameStatus = async ({ deliveryMode, client, loginId, appName, currentStatus }) => {
    const active = currentStatus ? 'false' : 'true';
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(`/topic/active-or-de-active/whatsapp/appName/queue/${loginId}`, (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') resolve({ active });
                else reject(new Error(data.MESSAGE || 'Failed to toggle app status'));
            });
            client.publish({ destination: '/app/active-or-de-active/whatsapp/appName/queue', body: JSON.stringify({ appName, active }) });
        });
    }
    const res = await apiFetch(`/active-or-de-active/whatsapp/appName/queue?appName=${appName}&active=${active}`, { method: 'PUT' });
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return { active };
    throw new Error(data.MESSAGE || 'Failed to toggle app status');
};

// ═══════════════════════════════════════════════════════════════════════════════
// SEND FORM — Vendors, App Names, Templates, Send
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fetch WhatsApp vendors (configs where isWhatsAppVendor=true).
 * REST:   GET /getAllWhatsAppConfig?isWhatsAppVendor=true
 * Socket: /app/getAllWhatsAppConfig  →  /topic/getAllWhatsAppConfig/{loginId}
 * Returns: string[] of vendor names
 */
export const fetchWhatsAppVendors = async ({ deliveryMode, client, loginId }) => {
    const normalizeVendors = (data) => {
        if (!Array.isArray(data)) return [];
        if (typeof data[0] === 'string') return data.filter(Boolean);
        return data.map(v => v.wa_vendor_name || v.vendorName).filter(Boolean);
    };

    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(`/topic/getAllWhatsAppConfig/${loginId}`, (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') resolve(normalizeVendors(data.data));
                else reject(new Error(data.MESSAGE || 'Failed to load vendors'));
            });
            client.publish({ destination: '/app/getAllWhatsAppConfig', body: JSON.stringify({ isWhatsAppVendor: true }) });
        });
    }

    const res = await apiFetch('/getAllWhatsAppConfig?isWhatsAppVendor=true');
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return normalizeVendors(data.data);
    throw new Error(data.MESSAGE || 'Failed to load vendors');
};

/**
 * Fetch WhatsApp app names.
 * REST:   GET /whatsapp/getAppName
 * Socket: /app/whatsapp/getAppName  →  /topic/whatsapp/getAppName/{loginId}
 * Returns: string[]
 */
export const fetchWhatsAppAppNames = async ({ deliveryMode, client, loginId }) => {
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(`/topic/whatsapp/getAppName/${loginId}`, (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') {
                    const list = Array.isArray(data.Response)
                        ? data.Response.map(i => i.appName || i).filter(Boolean)
                        : Array.isArray(data.data)
                            ? data.data.map(i => i.appName || i).filter(Boolean)
                            : [];
                    resolve(list);
                } else {
                    reject(new Error(data.message || 'Failed to load app names'));
                }
            });
            client.publish({ destination: '/app/whatsapp/getAppName', body: JSON.stringify({}) });
        });
    }
    const res = await apiFetch('/whatsapp/getAppName');
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') {
        const list = Array.isArray(data.Response)
            ? data.Response.map(i => i.appName || i).filter(Boolean)
            : Array.isArray(data.data)
                ? data.data.map(i => i.appName || i).filter(Boolean)
                : [];
        return list;
    }
    throw new Error(data.message || 'Failed to load app names');
};

/**
 * Fetch all WhatsApp templates for the send form dropdown.
 * REST:   GET /getWhatsAppTemplate
 * Socket: /app/getWhatsAppTemplate  →  /topic/getWhatsAppTemplate/{loginId}
 * Returns: template[] { _id, campaignId, templateId, message, creation_time_str }
 */
export const fetchWhatsAppTemplates = async ({ deliveryMode, client, loginId }) => {
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(`/topic/getWhatsAppTemplate/${loginId}`, (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') resolve(data.data || []);
                else reject(new Error(data.message || 'Failed to load templates'));
            });
            client.publish({ destination: '/app/getWhatsAppTemplate', body: JSON.stringify({}) });
        });
    }
    const res = await apiFetch('/getWhatsAppTemplate');
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return data.data || [];
    throw new Error(data.message || 'Failed to load templates');
};

/**
 * Send a WhatsApp notification.
 * REST:   POST /sendWhatsappNotification
 * Socket: /app/sendWhatsappNotification  →  /topic/sendWhatsappNotification/{loginId}
 *
 * Payload:
 * {
 *   Type:             "SINGLE" | "BULK",
 *   mobileNo:         string,
 *   campaignId:       string,
 *   appName:          string,
 *   attributeDetails: [{ attributeName: string, attributeValue: string }]
 * }
 */
export const sendWhatsAppNotification = async ({ deliveryMode, client, loginId }, payload) => {
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(`/topic/sendWhatsappNotification/${loginId}`, (msg) => {
                sub.unsubscribe();
                const data = parseSocket(msg);
                if (data.STATUS === 'SUCCESS') resolve(data);
                else reject(new Error(data.message || data.MESSAGE || 'Failed to send WhatsApp message'));
            });
            client.publish({ destination: '/app/sendWhatsappNotification', body: JSON.stringify(payload) });
        });
    }
    const res = await apiFetch('/sendWhatsappNotification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (data.STATUS === 'SUCCESS') return data;
    throw new Error(data.message || data.MESSAGE || 'Failed to send WhatsApp message');
};

// ═══════════════════════════════════════════════════════════════════════════════
// WHATSAPP HISTORY FILTER  ← NEW
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fetch paginated WhatsApp message history with optional filters.
 *
 * REST:   POST /whatsapp/filter
 * Socket: /app/whatsapp/filter  →  /topic/whatsapp/filter/{loginId}
 *
 * Filter payload (all fields optional except pageNo / pageSize):
 * {
 *   pageNo:            number,   // 0-based
 *   pageSize:          number,
 *   searchText?:       string,
 *   referenceIdOrName?:string,
 *   appName?:          string,
 *   vendorName?:       string,
 *   status?:           "SUCCESS" | "FAILED" | "PENDING",
 *   startTimeMs?:      string,   // epoch ms as string
 *   endTimeMs?:        string,
 * }
 *
 * Returns raw API data object:
 * { Response: WpRecord[], TOTAL_COUNT: number, STATUS: string, pageNo, pageSize }
 */
export const fetchWhatsAppHistoryFilter = async ({ deliveryMode, client, loginId }, filterPayload) => {
    if (deliveryMode === 'socket') {
        await ensureSocketConnection(client);
        return new Promise((resolve, reject) => {
            const sub = client.subscribe(`/topic/whatsapp/filter/${loginId}`, (msg) => {
                sub.unsubscribe();
                try {
                    const data = parseSocket(msg);
                    if (data?.Response && Array.isArray(data.Response)) {
                        resolve(data);
                    } else {
                        resolve({ Response: [], TOTAL_COUNT: 0 });
                    }
                } catch (e) {
                    reject(new Error('Failed to parse WhatsApp history socket response'));
                }
            });
            client.publish({
                destination: '/app/whatsapp/filter',
                body: JSON.stringify(filterPayload),
            });
        });
    }

    // ---- REST fallback ----
    const res = await apiFetch('/whatsapp/filter', {
        method: 'POST',
        body: JSON.stringify(filterPayload),
    });

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const data = await res.json();

    if (data?.Response && Array.isArray(data.Response)) return data;
    return { Response: [], TOTAL_COUNT: 0 };
};